import React, { useState } from 'react';
import styled from 'styled-components';

// Styled Components
const SectionWrapper = styled.section`
  padding: 100px 0;
  background-color: var(--bg-light);
`;

const SectionContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
`;

const SectionHeader = styled.div`
  text-align: center;
  max-width: 800px;
  margin: 0 auto 60px;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  color: var(--text-dark);
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SectionDescription = styled.p`
  font-size: 1.1rem;
  color: var(--text-light);
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const FaqContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const FaqItem = styled.div`
  background-color: var(--bg-white);
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 1rem;
  box-shadow: var(--shadow-sm);
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const FaqQuestion = styled.button`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border: none;
  background-color: transparent;
  cursor: pointer;
  text-align: left;
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-dark);
  transition: all var(--transition-fast);
  
  &:hover {
    color: var(--primary);
  }
  
  &:focus {
    outline: none;
  }
`;

const FaqAnswer = styled.div`
  padding: 0 1.5rem 1.5rem;
  color: var(--text-light);
  line-height: 1.6;
`;

// Icon components
const ChevronIcon = ({ isOpen }) => (
    <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0)',
            transition: 'transform 0.3s ease'
        }}
    >
        <polyline points="6 9 12 15 18 9"></polyline>
    </svg>
);

// FAQ Component
const FAQ = () => {
    // FAQ data
    const faqItems = [
        {
            id: 1,
            question: "建立行動購物應用需要多久時間？",
            answer: "根據專案複雜度和客製化程度的不同，一般需要 4-8 週的時間。我們採用敏捷開發方法，會分階段交付，讓您能夠盡早看到成果並提供反饋。"
        },
        {
            id: 2,
            question: "如何將我現有的電商系統資料遷移到新平台？",
            answer: "我們提供完整的資料遷移服務，包括商品資料、會員資料、訂單紀錄等。我們的技術團隊會與您密切合作，確保所有資料順利無損地轉移到新系統。"
        },
        {
            id: 3,
            question: "應用程式是否支援離線操作？",
            answer: "是的，我們的應用程式支援基本的離線瀏覽功能。客戶在網路不穩定的環境中也能查看已載入的商品資訊，提升使用體驗。一旦網路恢復，系統會自動同步最新資料。"
        },
        {
            id: 4,
            question: "提供哪些客戶支援服務？",
            answer: "我們提供多種支援服務，包括電子郵件、線上客服系統和電話支援。根據您選擇的方案不同，支援服務的範圍和優先級也有所不同。企業客戶更可享有專屬客戶經理和 24/7 技術支援。"
        }
    ];

    const [openItems, setOpenItems] = useState(new Set([1])); // Default open the first item

    const toggleItem = (id) => {
        const newOpenItems = new Set(openItems);
        if (newOpenItems.has(id)) {
            newOpenItems.delete(id);
        } else {
            newOpenItems.add(id);
        }
        setOpenItems(newOpenItems);
    };

    return (
        <SectionWrapper id="faq">
            <SectionContainer>
                <SectionHeader>
                    <SectionTitle>常見問題</SectionTitle>
                    <SectionDescription>
                        以下是客戶最常詢問的問題
                    </SectionDescription>
                </SectionHeader>

                <FaqContainer>
                    {faqItems.map(item => (
                        <FaqItem key={item.id}>
                            <FaqQuestion
                                onClick={() => toggleItem(item.id)}
                                aria-expanded={openItems.has(item.id)}
                            >
                                {item.question}
                                <ChevronIcon isOpen={openItems.has(item.id)} />
                            </FaqQuestion>

                            {openItems.has(item.id) && (
                                <FaqAnswer>
                                    {item.answer}
                                </FaqAnswer>
                            )}
                        </FaqItem>
                    ))}
                </FaqContainer>
            </SectionContainer>
        </SectionWrapper>
    );
};

export default FAQ;